/**
 * Utility Navigation Bar JavaScript
 * 
 * EXISTING VAT TOGGLE IMPLEMENTATION DOCUMENTATION:
 * ===================================================
 * 
 * Location: Found in dev/assets/betterconversions-addon.js and dev/assets/addons.js
 * Element: #price-toggle (checkbox input)
 * Storage: sessionStorage with key 'exclVAT'
 * 
 * How it works:
 * - Stores boolean value in sessionStorage: false = inc VAT (default), true = exc VAT
 * - Listens to 'change' event on #price-toggle checkbox
 * - Updates prices by finding all [data-price-wrapper] elements
 * - Toggles .hidden class on .in-vat and .ex-vat children:
 *   * If session_val is true (exc VAT): hides .in-vat, shows .ex-vat
 *   * If session_val is false (inc VAT): shows .in-vat, hides .ex-vat
 * 
 * This implementation replicates that exact functionality and syncs with it.
 */

(function() {
  'use strict';

  /**
   * UtilityNavVATToggle Class
   * Handles VAT toggle functionality with synchronization to existing toggle
   */
  class UtilityNavVATToggle {
    constructor(toggleElement) {
      // The toggleElement IS the button (has data-vat-toggle attribute)
      this.button = toggleElement;
      // Find the wrapper div (parent with class utility-nav__vat-toggle)
      this.toggleElement = toggleElement.closest('.utility-nav__vat-toggle') || toggleElement;
      
      this.optionState1 = this.button.querySelector('.utility-nav__vat-option--state-1');
      this.optionState2 = this.button.querySelector('.utility-nav__vat-option--state-2');
      this.icon = this.button.querySelector('.utility-nav__vat-icon');
      this.iconLeft = this.icon ? this.icon.querySelector('.utility-nav__vat-icon--left') : null;
      this.iconRight = this.icon ? this.icon.querySelector('.utility-nav__vat-icon--right') : null;
      this.slider = this.button.querySelector('.utility-nav__vat-slider');
      this.srOnly = document.querySelector('.utility-nav__sr-only');
      
      // Extract data attributes (button is guaranteed to exist since it's the toggleElement)
      this.cookieName = this.button.getAttribute('data-cookie-name') || 'exclVATv3';
      this.state1Value = this.button.getAttribute('data-state-1-value') || 'inc_vat';
      this.state2Value = this.button.getAttribute('data-state-2-value') || 'exc_vat';
      this.defaultState = this.button.getAttribute('data-default-state') || 'state_1';
      
      // Current state (maps to existing implementation: false = inc VAT, true = exc VAT)
      this.currentState = null;
      
      // Existing toggle reference (if present)
      this.existingToggle = document.querySelector('#price-toggle');
      
      // Event handler reference for cleanup
      this.vatToggleChangeHandler = null;
      
      // Resize observer for slider recalculation
      this.resizeObserver = null;
      this.resizeDebounceTimer = null;
    }

    /**
     * Initialize the toggle
     */
    init() {
      if (!this.button || !this.optionState1 || !this.optionState2 || !this.slider) {
        // console.warn('UtilityNavVATToggle: Required elements not found');
        return;
      }

      // Sync with existing toggle state first
      this.syncWithExistingToggle();

      // Load saved state or use default
      const savedState = this.getSavedState();
      const initialState = savedState !== null ? savedState : this.defaultState;
      
      // Set initial state
      this.setState(initialState, false);
      
      // Calculate slider dimensions after a brief delay to ensure fonts are loaded
      // Use requestAnimationFrame for next frame, then setTimeout for additional delay
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.calculateSliderPosition();
        }, 100);
      });
      
      // Bind event listeners
      this.bindEvents();
      
      // Listen for resize events (debounced)
      this.setupResizeObserver();
      
      // Sync with existing toggle changes
      if (this.existingToggle) {
        this.syncWithExistingToggle();
        this.observeExistingToggle();
      }

      // Listen for VAT toggle changes from other instances
      this.setupCrossToggleSync();
    }

    /**
     * Sync with existing VAT toggle implementation
     * Reads the existing toggle's state and matches it
     */
    syncWithExistingToggle() {
      if (!this.existingToggle) {
        return;
      }

      try {
        // Read existing toggle's storage
        const sessionKey = 'exclVATv3';
        const sessionVal = sessionStorage.getItem(sessionKey);
        
        if (sessionVal !== null) {
          // Map existing implementation values to our states
          // false/inc VAT = state_1, true/exc VAT = state_2
          const isExcVAT = sessionVal === 'true';
          const targetState = isExcVAT ? 'state_2' : 'state_1';
          
          if (this.currentState !== targetState) {
            this.setState(targetState, false);
            this.calculateSliderPosition();
          }
        }
      } catch (error) {
        // console.warn('UtilityNavVATToggle: Error syncing with existing toggle', error);
      }
    }

    /**
     * Observe existing toggle for changes
     */
    observeExistingToggle() {
      if (!this.existingToggle) {
        return;
      }

      // Listen to change events on existing toggle
      this.existingToggle.addEventListener('change', () => {
        this.syncWithExistingToggle();
        this.calculateSliderPosition();
      });

      // Also watch for programmatic changes via MutationObserver
      const observer = new MutationObserver(() => {
        this.syncWithExistingToggle();
        this.calculateSliderPosition();
      });

      observer.observe(this.existingToggle, {
        attributes: true,
        attributeFilter: ['checked']
      });
    }

    /**
     * Setup cross-toggle synchronization
     * Listens for VAT toggle changes from other instances on the page
     */
    setupCrossToggleSync() {
      // Store handler reference for cleanup
      this.vatToggleChangeHandler = (e) => {
        // Don't react to our own events
        const sourceButton = e.detail?.source || e.target;
        if (sourceButton === this.button) {
          return;
        }

        // Get the new state from the event detail
        const newState = e.detail?.state;
        
        // Only update if the state is different and valid
        if (newState && (newState === 'state_1' || newState === 'state_2') && newState !== this.currentState) {
          // Update state without saving (to avoid triggering another event)
          // The state is already saved by the source toggle
          this.setState(newState, true);
          // Recalculate slider position
          this.calculateSliderPosition();
        }
      };

      // Listen on both document and button for maximum compatibility
      document.addEventListener('vatToggleChange', this.vatToggleChangeHandler);
      this.button.addEventListener('vatToggleChange', this.vatToggleChangeHandler);

      // Also listen for storage events (for cross-tab sync, though storage events
      // only fire in other tabs/windows, not same window)
      window.addEventListener('storage', (e) => {
        if (e.key === this.cookieName) {
          const savedState = this.getSavedState();
          if (savedState !== null && savedState !== this.currentState) {
            this.setState(savedState, true);
            this.calculateSliderPosition();
          }
        }
      });
    }

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Click on entire button
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleToggle();
      });

      // Click on state 1 option
      this.optionState1.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.currentState !== 'state_1') {
          this.setState('state_1', true);
          this.saveState('state_1');
          this.triggerStateChange();
          this.announceToScreenReaders(this.optionState1.textContent.trim());
        }
      });

      // Click on state 2 option
      this.optionState2.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.currentState !== 'state_2') {
          this.setState('state_2', true);
          this.saveState('state_2');
          this.triggerStateChange();
          this.announceToScreenReaders(this.optionState2.textContent.trim());
        }
      });

      // Click on icon (if present)
      if (this.icon) {
        this.icon.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleToggle();
        });
      }

      // Keyboard support
      this.button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleToggle();
        }
      });
    }

    /**
     * Handle toggle action
     */
    handleToggle() {
      const newState = this.currentState === 'state_1' ? 'state_2' : 'state_1';
      this.setState(newState, true);
      this.saveState(newState);
      this.triggerStateChange();
      
      const activeLabel = newState === 'state_1' 
        ? this.optionState1.textContent.trim() 
        : this.optionState2.textContent.trim();
      this.announceToScreenReaders(activeLabel);
    }

    /**
     * Set the toggle state
     * @param {string} state - 'state_1' or 'state_2'
     * @param {boolean} animate - Whether to animate the transition
     */
    setState(state, animate = true) {
      if (state !== 'state_1' && state !== 'state_2') {
        // console.warn('UtilityNavVATToggle: Invalid state', state);
        return;
      }

      this.currentState = state;
      
      // Update button data attribute
      this.button.setAttribute('data-active-state', state);
      
      // Update aria-current attributes
      this.optionState1.setAttribute('aria-current', state === 'state_1' ? 'true' : 'false');
      this.optionState2.setAttribute('aria-current', state === 'state_2' ? 'true' : 'false');

      // Utility bar restyle (2026-10-02): the desktop bar's toggle is a role="switch" ("Show prices exc. VAT");
      // keep aria-checked in step. Toggles without the role (mobile header) are unaffected.
      if (this.button.getAttribute('role') === 'switch') {
        this.button.setAttribute('aria-checked', state === 'state_2' ? 'true' : 'false');
      }

      // Update icon visibility based on state
      // state_1 = inc VAT (left position) = show left icon
      // state_2 = exc VAT (right position) = show right icon
      if (this.iconLeft && this.iconRight) {
        if (state === 'state_1') {
          // Show left icon (Group 17), hide right icon
          this.iconLeft.style.display = 'block';
          this.iconRight.style.display = 'none';
        } else {
          // Show right icon (toggleright), hide left icon
          this.iconLeft.style.display = 'none';
          this.iconRight.style.display = 'block';
        }
      }
      
      // Temporarily disable transitions if not animating
      if (!animate) {
        this.slider.style.transition = 'none';
      }
      
      // Recalculate and update slider position
      this.calculateSliderPosition();
      
      // Re-enable transitions after a frame
      if (!animate) {
        requestAnimationFrame(() => {
          this.slider.style.transition = '';
        });
      }
    }

    /**
     * Calculate slider position and width
     */
    calculateSliderPosition() {
      if (!this.slider || !this.optionState1 || !this.optionState2) {
        return;
      }

      // Measure text widths
      const state1Rect = this.optionState1.getBoundingClientRect();
      const state2Rect = this.optionState2.getBoundingClientRect();
      
      // Measure icon width (if present)
      let iconWidth = 0;
      let iconGap = 0;
      if (this.icon) {
        const iconRect = this.icon.getBoundingClientRect();
        iconWidth = iconRect.width;
        iconGap = 8; // Gap between elements
      }

      // Get button padding for calculations
      const buttonStyle = window.getComputedStyle(this.button);
      const paddingLeft = parseFloat(buttonStyle.paddingLeft) || 12;
      const paddingRight = parseFloat(buttonStyle.paddingRight) || 12;
      const gap = parseFloat(buttonStyle.gap) || 8;

      // Calculate slider width and position based on active state
      let sliderWidth;
      let translateX;

      if (this.currentState === 'state_1') {
        // Active state is state_1 (left/inc VAT)
        sliderWidth = state1Rect.width;
        translateX = paddingLeft;
      } else {
        // Active state is state_2 (right/exc VAT)
        sliderWidth = state2Rect.width;
        translateX = paddingLeft + state1Rect.width + (iconWidth > 0 ? iconWidth + gap : 0);
      }

      // Set slider dimensions and position
      this.slider.style.width = `${sliderWidth}px`;
      this.slider.style.transform = `translateY(-50%) translateX(${translateX}px)`;
    }

    /**
     * Setup resize observer for slider recalculation
     */
    setupResizeObserver() {
      // Debounce resize calculations
      const handleResize = () => {
        if (this.resizeDebounceTimer) {
          clearTimeout(this.resizeDebounceTimer);
        }
        
        this.resizeDebounceTimer = setTimeout(() => {
          this.calculateSliderPosition();
        }, 150);
      };

      // Use ResizeObserver if available
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(handleResize);
        this.resizeObserver.observe(this.button);
      } else {
        // Fallback to window resize
        window.addEventListener('resize', handleResize);
      }
    }

    /**
     * Trigger state change events and update prices
     * This replicates the exact functionality of the existing toggle
     */
    triggerStateChange() {
      // Map our states to existing implementation format
      // state_1 = inc VAT = false, state_2 = exc VAT = true
      const isExcVAT = this.currentState === 'state_2';
      
      // Update existing toggle checkbox if it exists
      if (this.existingToggle) {
        this.existingToggle.checked = isExcVAT;
        
        // Trigger change event on existing toggle to ensure other listeners fire
        const changeEvent = new Event('change', { bubbles: true });
        this.existingToggle.dispatchEvent(changeEvent);
        
        // Also trigger jQuery change event if jQuery is available (for existing implementation)
        if (typeof window.jQuery !== 'undefined' || typeof window.$ !== 'undefined') {
          const $ = window.jQuery || window.$;
          $(this.existingToggle).trigger('change');
        }
      } else {
        // If no existing toggle, manually update prices
        this.updatePrices(isExcVAT);
      }

      // Dispatch custom event for other listeners (dispatch on button so we can identify the source)
      const customEvent = new CustomEvent('vatToggleChange', {
        detail: {
          mode: isExcVAT ? 'exc' : 'inc',
          isIncVAT: !isExcVAT,
          isExcVAT: isExcVAT,
          state: this.currentState,
          value: isExcVAT ? this.state2Value : this.state1Value,
          source: this.button
        },
        bubbles: true
      });
      
      // Dispatch on button first, then let it bubble to document
      this.button.dispatchEvent(customEvent);
      // Also dispatch on document for broader compatibility
      document.dispatchEvent(customEvent);

      // Update body class (if needed by theme)
      document.body.classList.toggle('vat-inc', !isExcVAT);
      document.body.classList.toggle('vat-exc', isExcVAT);
    }

    /**
     * Update prices manually (replicates existing setPrice function)
     * @param {boolean} isExcVAT - Whether to show exc VAT prices
     */
    updatePrices(isExcVAT) {
      try {
        const priceWrappers = document.querySelectorAll('[data-price-wrapper]');
        
        priceWrappers.forEach((wrapper) => {
          const inVatElements = wrapper.querySelectorAll('.in-vat');
          const exVatElements = wrapper.querySelectorAll('.ex-vat');
          
          if (isExcVAT) {
            // Show exc VAT, hide inc VAT
            inVatElements.forEach(el => el.classList.add('hidden'));
            exVatElements.forEach(el => el.classList.remove('hidden'));
          } else {
            // Show inc VAT, hide exc VAT
            exVatElements.forEach(el => el.classList.add('hidden'));
            inVatElements.forEach(el => el.classList.remove('hidden'));
          }
        });
      } catch (error) {
        // console.warn('UtilityNavVATToggle: Error updating prices', error);
      }
    }

    /**
     * Save state to sessionStorage (matches existing implementation)
     * @param {string} state - 'state_1' or 'state_2'
     */
    saveState(state) {
      try {
        // Map our states to existing implementation format
        // state_1 = inc VAT = false, state_2 = exc VAT = true
        const isExcVAT = state === 'state_2';
        
        // Save to sessionStorage with same key as existing implementation
        sessionStorage.setItem(this.cookieName, isExcVAT.toString());
      } catch (error) {
        // console.warn('UtilityNavVATToggle: Error saving state', error);
      }
    }

    /**
     * Get saved state from sessionStorage (matches existing implementation)
     * @returns {string|null} - 'state_1', 'state_2', or null if not found
     */
    getSavedState() {
      try {
        const savedValue = sessionStorage.getItem(this.cookieName);
        
        if (savedValue === null) {
          return null;
        }
        
        // Map existing implementation format to our states
        // false/inc VAT = state_1, true/exc VAT = state_2
        const isExcVAT = savedValue === 'true';
        return isExcVAT ? 'state_2' : 'state_1';
      } catch (error) {
        // console.warn('UtilityNavVATToggle: Error reading saved state', error);
        return null;
      }
    }

    /**
     * Announce state change to screen readers
     * @param {string} label - The label text to announce
     */
    announceToScreenReaders(label) {
      if (!this.srOnly) {
        return;
      }

      this.srOnly.textContent = `Switched to ${label}`;
      
      // Clear after announcement
      setTimeout(() => {
        if (this.srOnly) {
          this.srOnly.textContent = '';
        }
      }, 1000);
    }

    /**
     * Cleanup method
     */
    destroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }

      // Remove cross-toggle sync event listeners
      if (this.vatToggleChangeHandler) {
        document.removeEventListener('vatToggleChange', this.vatToggleChangeHandler);
        if (this.button) {
          this.button.removeEventListener('vatToggleChange', this.vatToggleChangeHandler);
        }
      }
    }
  }

  /**
   * Initialize all VAT toggles on the page
   */
  function initUtilityNavVATToggles() {
    const toggleElements = document.querySelectorAll('[data-vat-toggle]');
    
    toggleElements.forEach((toggleElement) => {
      if (!toggleElement) {
        return;
      }
      
      const toggle = new UtilityNavVATToggle(toggleElement);
      
      // Only initialize if required elements exist
      if (toggle.button && toggle.optionState1 && toggle.optionState2 && toggle.slider) {
        toggle.init();
        // Store reference for cleanup if needed
        toggleElement._utilityNavToggle = toggle;
      } else {
        // console.warn('UtilityNavVATToggle: Skipping initialization - required elements missing', toggleElement);
      }
    });
  }

  /**
   * Small text slider inside the utility nav (left-aligned, capped width)
   */
  class UtilityNavMiniSlider {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.slider = wrapper ? wrapper.querySelector('[data-utility-mini-slider]') : null;
      this.track = this.slider ? this.slider.querySelector('.utility-nav__slider-track') : null;
      this.slides = this.track ? Array.from(this.track.querySelectorAll('.utility-nav__slider-slide')) : [];

      if (!this.slider || !this.track || this.slides.length <= 1) {
        return;
      }

      this.interval = parseInt(this.slider.getAttribute('data-interval'), 10) || 4000;
      this.autoplay = this.slider.getAttribute('data-autoplay') === 'true';
      this.pauseOnHover = this.slider.getAttribute('data-pause-on-hover') === 'true';

      this.currentIndex = 0;
      this.timer = null;
      this.isPaused = false;
      this.resizeHandler = null;
    }

    init() {
      if (this.slides.length <= 1) return;
      // Set widths immediately to prevent flash
      this.setWidths();
      // Ensure initial position
      this.goTo(0);
      this.bindEvents();
      if (this.autoplay) {
        this.start();
      }
    }

    bindEvents() {
      if (this.pauseOnHover && this.slider) {
        this.slider.addEventListener('mouseenter', () => this.pause());
        this.slider.addEventListener('mouseleave', () => this.resume());
      }

      this.resizeHandler = () => {
        this.setWidths();
        this.goTo(this.currentIndex);
      };
      window.addEventListener('resize', this.resizeHandler);
    }

    goTo(index) {
      if (!this.track || this.slides.length <= 1) return;

      const targetIndex =
        index < 0
          ? this.slides.length - 1
          : index >= this.slides.length
            ? 0
            : index;

      const slideWidth = this.slider.getBoundingClientRect().width;
      this.track.style.transform = `translate3d(-${targetIndex * slideWidth}px, 0, 0)`;

      this.slides.forEach((slide, idx) => {
        const isActive = idx === targetIndex;
        slide.classList.toggle('is-active', isActive);
        if (isActive) {
          slide.setAttribute('data-active', 'true');
        } else {
          slide.removeAttribute('data-active');
        }
      });

      this.currentIndex = targetIndex;
    }

    start() {
      this.stop();
      this.timer = setInterval(() => {
        if (!this.isPaused) {
          this.goTo(this.currentIndex + 1);
        }
      }, this.interval);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.isPaused = true;
    }

    resume() {
      this.isPaused = false;
    }

    destroy() {
      this.stop();
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
    }

    setWidths() {
      const slideWidth = this.slider.getBoundingClientRect().width;
      if (!slideWidth) {
        // Retry after a short delay if slider isn't ready yet
        setTimeout(() => this.setWidths(), 50);
        return;
      }

      this.track.style.width = `${slideWidth * this.slides.length}px`;
      this.slides.forEach((slide) => {
        slide.style.width = `${slideWidth}px`;
        slide.style.flex = `0 0 ${slideWidth}px`;
        slide.style.minWidth = `${slideWidth}px`;
        slide.style.maxWidth = `${slideWidth}px`;
      });
      
      // Ensure initial position is correct
      this.track.style.transform = `translate3d(0, 0, 0)`;
    }
  }

  function initUtilityNavMiniSliders() {
    const wrappers = document.querySelectorAll('[data-mini-slider-wrapper]');

    wrappers.forEach((wrapper) => {
      if (wrapper._utilityNavMiniSlider) {
        return;
      }

      const slider = new UtilityNavMiniSlider(wrapper);
      // Use requestAnimationFrame to ensure DOM is ready and prevent flash
      requestAnimationFrame(() => {
        slider.init();
      });
      wrapper._utilityNavMiniSlider = slider;
    });
  }
  
  /**
   * Wait for DOM to be ready
   */
  function domReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /**
   * Initialize when DOM is ready
   */
  domReady(() => {
    initUtilityNavMiniSliders();
    initUtilityNavVATToggles();
  });
  
  // Also try to initialize immediately if script loads after DOM is ready
  if (document.readyState !== 'loading') {
    requestAnimationFrame(() => {
      initUtilityNavMiniSliders();
    });
  }

  /**
   * Re-initialize when theme editor loads/reloads sections
   */
  if (typeof Shopify !== 'undefined') {
    document.addEventListener('shopify:section:load', (event) => {
      if (event.detail.sectionId) {
        const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
        if (section) {
          const toggleElement = section.querySelector('[data-vat-toggle]');
          const miniSliderWrapper = section.querySelector('[data-mini-slider-wrapper]');

          if (miniSliderWrapper && !miniSliderWrapper._utilityNavMiniSlider) {
            const slider = new UtilityNavMiniSlider(miniSliderWrapper);
            slider.init();
            miniSliderWrapper._utilityNavMiniSlider = slider;
          }

          if (toggleElement && !toggleElement._utilityNavToggle) {
            const toggle = new UtilityNavVATToggle(toggleElement);
            // Only initialize if required elements exist
            if (toggle.button && toggle.optionState1 && toggle.optionState2 && toggle.slider) {
              toggle.init();
              toggleElement._utilityNavToggle = toggle;
            }
          }
        }
      }
    });

    document.addEventListener('shopify:section:reorder', () => {
      // Cleanup existing toggles and reinitialize
      document.querySelectorAll('[data-vat-toggle]').forEach((toggleElement) => {
        if (toggleElement._utilityNavToggle) {
          toggleElement._utilityNavToggle.destroy();
          delete toggleElement._utilityNavToggle;
        }
      });

      document.querySelectorAll('[data-mini-slider-wrapper]').forEach((wrapper) => {
        if (wrapper._utilityNavMiniSlider) {
          wrapper._utilityNavMiniSlider.destroy();
          delete wrapper._utilityNavMiniSlider;
        }
      });
      
      initUtilityNavMiniSliders();
      initUtilityNavVATToggles();
    });
  }

  // Export for potential external access
  if (typeof window !== 'undefined') {
    window.UtilityNavVATToggle = UtilityNavVATToggle;
  }
})();

